import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as moment from 'moment';
import {RouteComponentProps} from 'react-router';
import EmptyStateContainer from '../EmptyState/EmptyStateContainer';
import ReviewService from '../../services/ReviewService/ReviewService';
import Strings from '../../globals/Strings';
import Constants from '../../globals/Constants';
import ErrorMessageModel from '../../types/ErrorMessageModel';
import ReviewContainer from '../Review/ReviewContainer';
import UserModel from '../../types/UserModel';
import SkeletonPage from '../SkeletonPage/SkeletonPage';
import CommentModel from '../../types/CommentModel';
import IVideoPlayer from '../../types/IVideoPlayer';
import ReviewRouterService from '../../services/ReviewRouterService/ReviewRouterService';
import MyWindow from '../../types/MyWindow';
import ClientSideError from '../ClientSideError/ClientSideError';
import Log from '../../utils/Log';
import UserMessaging from '../../utils/UserMessaging';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import ContentTypeParser from '../../utils/ContentTypeParser/ContentTypeParser';
import ParamsModel from '../../types/ParamsModel';

declare const window: MyWindow;

export default class ReviewLoader extends React.Component<ReviewLoaderProps, ReviewLoaderState> {
   readonly state: ReviewLoaderState = {errorMessaging: null, loadingData: true, versionLoading: false};
   intervalId: number;
   errorRecoveryIntervalId: number = -1;

   constructor(props: ReviewLoaderProps) {
      super(props);
      autoBind(this);
   }

   componentDidMount() {
      const {reviewId, versionNumber} = this.props;
      this.loadReview(reviewId, versionNumber);

      const isVideoLoading = this.getIsVideoLoading(this.props);
      if (!isVideoLoading) {
         this.onReviewReady();
      }
   }

   componentWillUnmount() {
      this.reset();
      this.errorRecoveryIntervalId !== -1 && window.clearInterval(this.errorRecoveryIntervalId);
   }

   componentDidUpdate(prevProps: ReviewLoaderProps, prevState: ReviewLoaderState) {
      const {reviewId, versionNumber} = this.props;

      if (reviewId !== prevProps.reviewId || versionNumber !== prevProps.versionNumber) {
         this.setState({errorMessaging: null});
         this.loadReview(reviewId, versionNumber);
      }

      if (!this.getIsVideo(this.props.currentReviewVersion)) {
         if (prevState.loadingData && !this.state.loadingData) {
            this.onReviewReady();
         }
      } else {
         if (this.getIsVideoLoading(prevProps) && !this.getIsVideoLoading(this.props)) {
            this.onReviewReady();
         }
      }
   }

   getIsVideoLoading(newProps: ReviewLoaderProps) {
      return !newProps.currentReviewVersion || this.getIsVideo(newProps.currentReviewVersion) && newProps.player.getIsNullInstance();
   }

   getIsVideo(version: ReviewVersionModel): boolean {
      return version && ContentTypeParser.getContentTypeIsVideo(version.MediaContentType);
   }

   async loadReview(reviewId: string, versionNumber: number): Promise<void> {
      this.reset();
      this.setState({versionLoading: true});
      this.props.setReviewId(reviewId, versionNumber);
      this.props.setPageIsLoading(true);
      try {
         await this.loadReviewAndComments(reviewId, versionNumber);
         this.startPoll();
      } catch (e) {
         this.setState({loadingData: false});
      }
   }

   reset(): void {
      if (this.intervalId) {
         window.clearInterval(this.intervalId);
      }

      this.props.resetReview();
   }

   startPoll(): void {
      this.intervalId = window.setInterval(async () => {
         const {currentReviewVersion, mostRecentCommentUpdate} = this.props;
         try {
            const lastRequestDate = mostRecentCommentUpdate ? moment(mostRecentCommentUpdate).format() : null;
            await ReviewService.loadComments(currentReviewVersion, lastRequestDate);
         } catch (e) {
            if (e && (e.status === Constants.statusCodes.notFound || e.status === Constants.statusCodes.forbidden)) {
               window.clearInterval(this.intervalId);
               Log.warn('Detected that review was deleted. Stopping comment polling loop.');
               UserMessaging.warn(Strings.errors.reviewDeleted);
            }
         }
      }, Constants.refreshIntervalInMs);
   }

   startErrorRecoveryPoll(): void {
      this.errorRecoveryIntervalId = window.setInterval(async () => {
         const {reviewId, versionNumber} = this.props;
         try {
            await this.loadReviewAndComments(reviewId, versionNumber);
         } catch (e) {
            Log.warn('Error in startErrorRecoveryPoll: ' + e);
         }
      }, Constants.refreshIntervalInMs);
   }

   async loadReviewAndComments(reviewId: string, versionNumber: number): Promise<void> {
      try {
         await ReviewService.loadReview(reviewId, versionNumber);
         await this.loadVersionComments();
         this.setState({loadingData: false});
         if (this.errorRecoveryIntervalId !== -1) {
            window.clearInterval(this.errorRecoveryIntervalId);
            this.errorRecoveryIntervalId = -1;
            this.startPoll();
         }
      } catch (e) {
         let errorMessaging;
         if (e.status === Constants.statusCodes.forbidden) {
            const errorReason = e.reason || '';
            const removedReviewer = errorReason.indexOf(Constants.errorReasons.removedReviewer) !== -1;
            const email = this.props.currentUser.email || Strings.errors.noEmailAvailable;
            errorMessaging = {
               title: removedReviewer ? Strings.errors.noLongerReviewer.replace('{param0}', email) : Strings.errors.cannotAccessReview.replace('{param0}', email),
               detail: removedReviewer ? Strings.errors.noLongerReviewerDetail : Strings.errors.cannotAccessReviewDetail,
               imagePath: Constants.assetLinks.lockImage
            };
         } else if (e.status === Constants.statusCodes.notFound || e.message === Constants.invalidVersionNumberError) {
            errorMessaging = {
               title: Strings.errors.pageError,
               detail: Strings.errors.pageErrorDetail,
               statusCode: Constants.statusCodes.notFound,
               versionNotFound: true
            };
         } else if (e.message === 'Network Error') {
            if (this.errorRecoveryIntervalId === -1) {
               UserMessaging.warn(Strings.errors.failedUpdateReview);
               this.startErrorRecoveryPoll();
            }
         } else {
            errorMessaging = {
               title: Strings.errors.serverError,
               detail: Strings.errors.serverErrorDetail,
               statusCode: Constants.statusCodes.serverError
            };
         }
         this.setState({errorMessaging, loadingData: false});
         this.props.setPageIsLoading(false);
         throw e;
      }
   }

   async loadVersionComments(): Promise<void> {
      const {currentReviewVersion, resetReview, setPageIsLoading} = this.props;
      resetReview();
      await ReviewService.loadComments(currentReviewVersion);
      this.setState({versionLoading: false});
      setPageIsLoading(false);
   }

   onReviewReady() {
      const {setSelectedComment, player, comments, commentId, reviewId, versionNumber, history} = this.props;

      if (commentId) {
         const selectedComment = comments && comments.find(c => c.id === commentId);

         if (selectedComment) {
            setSelectedComment(commentId);
            player.setCurrentTime(selectedComment.time);
            ReviewRouterService.navigateToReviewOnTimeMismatch(history, player, reviewId, versionNumber, selectedComment.id, selectedComment.time);
         } else {
            ReviewRouterService.navigateToReview(history, reviewId, versionNumber);
         }
      }
   }

   render() {
      const {errorMessaging, loadingData, versionLoading} = this.state;
      const showErrorPage = errorMessaging && errorMessaging.statusCode;
      const showEmptyState = errorMessaging && !showErrorPage;
      const showReview = !loadingData && !errorMessaging;

      if (showErrorPage) {
         return <ClientSideError title={errorMessaging.title} text1={errorMessaging.detail} showHeaderButtons={true} statusCode={errorMessaging.statusCode} versionNotFound={errorMessaging.versionNotFound}/>;
      } else if (showEmptyState) {
         return <SkeletonPage showHeaderButtons={true}><EmptyStateContainer title={errorMessaging.title} text={errorMessaging.detail} imageSource={errorMessaging.imagePath}/></SkeletonPage>;
      } else if (showReview) {
         return <ReviewContainer versionLoading={versionLoading}/>;
      }
      return <SkeletonPage/>;
   }
}

export interface ReviewLoaderProps extends RouteComponentProps<ParamsModel> {
   reviewId: string;
   commentId: string;
   mostRecentCommentUpdate: Date;
   currentUser: UserModel;
   comments: CommentModel[];
   player: IVideoPlayer;
   versionNumber: number;
   currentReviewVersion: ReviewVersionModel;
   resetReview(): void;
   setPageIsLoading(pageIsLoading: boolean): void;
   setSelectedComment(commentId: string): void;
   setReviewId(reviewId: string, versionNumber: number): void;
}

export interface ReviewLoaderState {
   errorMessaging?: ErrorMessageModel;
   loadingData: boolean;
   versionLoading: boolean;
}
